/* eslint-disable no-alert */
/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaEdit, FaInstagram, FaTrash } from 'react-icons/fa';
import { MdLocationPin } from 'react-icons/md';
import { RiAccountPinBoxLine } from 'react-icons/ri';
import { useHistory } from 'react-router-dom';

import LoadingContainer from 'components/LoadingContainer';
import { useUserData } from 'hooks/auth';
import { useSearch } from 'hooks/search';
import { Review } from 'interfaces/Review';
import Service from 'interfaces/Service';
import { db } from 'services/firebase';
import { isUserAdmin } from 'utils/permissions';

import PhoneDetails from './PhoneDetails';
import Reviews from './Reviews';
import {
  Container,
  ServiceInfo,
  EditServiceButton,
  DeleteServiceButton,
  CategoryImages,
  InstagramBox,
  LocationContainer,
  ActionButton,
} from './styles';
import WriteReview from './WriteReview';

const ServiceDetails: React.FC = () => {
  const history = useHistory();
  const { clearSearch } = useSearch();

  const { user, userVerified } = useUserData();
  const [userHasEditPermission, setUserHasEditPermission] = useState(false);

  const [loading, setLoading] = useState(false);
  const [iconLoaded, setIconLoaded] = useState(false);
  const [serviceId, setServiceId] = useState<string>();
  const [service, setService] = useState<Service>();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewModeActive, setReviewModeActive] = useState(false);

  const userHasWrittenReview = useMemo(
    () => !!reviews.find((review) => review.author.id === user?.uid),
    [reviews, user],
  );

  const editService = useCallback(async () => {
    history.push(`${serviceId}/edit`);
  }, [history, serviceId]);

  const deleteService = useCallback(async () => {
    if (
      window.confirm(
        'Você realmente deseja deletar este serviço? Esta ação é irreversível',
      )
    ) {
      if (!serviceId) {
        toast.error('Ocorreu um erro inesperado. Tente novamente');
        return;
      }

      try {
        await deleteDoc(doc(db, 'services', serviceId));
        clearSearch();
        history.push('/');
        toast.success('Serviço deletado com sucesso');
      } catch (err) {
        toast.error(
          `Erro inesperado ao deletar serviço.\n Causa: ${
            (err as Error).message
          }`,
        );
      }
    }
  }, [clearSearch, history, serviceId]);

  useEffect(() => {
    const loadUserPermissions = async (): Promise<void> => {
      setUserHasEditPermission(await isUserAdmin(user?.uid));
    };

    loadUserPermissions();
  }, [user]);

  useEffect(() => {
    const loadServiceData = async (): Promise<void> => {
      setLoading(true);

      const currentUrl = window.location.href;
      const id = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);

      setServiceId(id);

      const serviceDoc = await getDoc(doc(db, 'services', id));
      const serviceData = serviceDoc.data() as Service;

      if (!serviceData) {
        toast.error(
          'Erro ao carregar informações do serviço. Recarregue a página e tente novamente',
          { duration: 2000 },
        );
        history.push('/');
        return;
      }

      const q = query(collection(db, `/services/${id}/reviews`));
      const reviewsSnapshot = await getDocs(q);
      const reviewData = reviewsSnapshot.docs.map((document) => ({
        ...document.data(),
        id: document.id,
      })) as Review[];

      setService(serviceData);
      setReviews(reviewData);
      setLoading(false);
    };

    loadServiceData();
  }, [history, serviceId]);

  return (
    <Container>
      {loading ? (
        <LoadingContainer color="#fff" />
      ) : (
        !!service && (
          <>
            <ServiceInfo>
              {userHasEditPermission && (
                <EditServiceButton onClick={editService}>
                  <FaEdit size={30} />
                </EditServiceButton>
              )}
              {userHasEditPermission && (
                <DeleteServiceButton onClick={deleteService}>
                  <FaTrash size={30} />
                </DeleteServiceButton>
              )}

              <CategoryImages>
                {service.categories.map((category) => (
                  <img
                    key={category.id}
                    style={{ display: iconLoaded ? 'block' : 'none' }}
                    src={`https://firebasestorage.googleapis.com/v0/b/mycircle-app.appspot.com/o/category-icons%2Fwhite%2F${category.id}.png?alt=media&token=f3945487-13da-4e9e-aa0e-cda148d89baa`}
                    alt={category.readableName}
                    width={70}
                    onLoad={() => setIconLoaded(true)}
                  />
                ))}
              </CategoryImages>
              {!iconLoaded && <LoadingContainer color="#fff" />}

              <h3>{service.name}</h3>

              {service.instagram.trim() !== '' && (
                <InstagramBox
                  to={{
                    pathname: `https://www.instagram.com/${service.instagram}`,
                  }}
                  target="_blank"
                >
                  <FaInstagram size={20} />
                  <div style={{ width: '5px' }} />
                  <h4>{service.instagram}</h4>
                </InstagramBox>
              )}

              <h5>{service.description}</h5>

              <PhoneDetails service={service} />

              {service.location && (
                <LocationContainer>
                  <MdLocationPin />
                  <div style={{ width: 10 }} />
                  <h5>{service.location}</h5>
                </LocationContainer>
              )}
            </ServiceInfo>

            <h2>
              Avaliações{' '}
              {userHasEditPermission && (
                <>
                  <div style={{ width: 10 }} />
                  <FaEdit
                    size={25}
                    onClick={() =>
                      history.push(`/services/${serviceId}/edit/reviews`)
                    }
                  />
                </>
              )}
            </h2>

            {reviewModeActive ? (
              <WriteReview
                setReviewModeActive={setReviewModeActive}
                setReviews={setReviews}
              />
            ) : (
              <>
                {userVerified && !userHasWrittenReview ? (
                  <ActionButton
                    type="button"
                    onClick={() => setReviewModeActive(true)}
                  >
                    Adicionar avaliação
                    <div style={{ width: 20 }} />
                    <FaEdit />
                  </ActionButton>
                ) : !user ? (
                  <ActionButton
                    type="button"
                    onClick={() => history.push('/signin')}
                  >
                    <p>
                      Clique <b>aqui</b> e cadastre-se para fazer a sua
                      avaliação
                    </p>
                    <RiAccountPinBoxLine size={50} />
                  </ActionButton>
                ) : (
                  !userVerified && (
                    <ActionButton
                      type="button"
                      onClick={() =>
                        window.location.replace(
                          'https://2rht68app8p.typeform.com/circle-verify?typeform-source=localhost',
                        )
                      }
                    >
                      <p style={{ margin: 0 }}>
                        Sua conta ainda não foi verificada. Você pode solicitar
                        a verificação clicando <b>aqui</b>
                      </p>
                    </ActionButton>
                  )
                )}
                <Reviews reviews={reviews} setReviews={setReviews} />
              </>
            )}
          </>
        )
      )}
    </Container>
  );
};

export default ServiceDetails;
